/**
 * Form Handler types
 * Public lead-capture endpoints that accept POST submissions from 3rd-party form builders.
 */

import type { FieldMapping as TemplateMergeFieldMapping } from "../services/templates.service";

export interface FieldMapping {
  form_field: string;
  contact_field: string;
}

// Re-exported under a non-colliding name so callers of formHandler.types
// can reference both: the form-handler FieldMapping (form_field →
// contact_field) and the template-side mapping (templateField →
// sourceType/sourceField/lookupId/etc.) used by the autoresponder
// variable-mappings feature.
export type MergeFieldMapping = TemplateMergeFieldMapping;

// Subset of sourceType allowed for form-handler autoresponder mappings.
// The campaign-flight resolver supports voucher/content/url/sendgrid/
// landing_page in addition, but none have a meaningful source in the
// form-handler send context — rejected at validation time.
export const FORM_HANDLER_ALLOWED_SOURCE_TYPES = [
  "contact",
  "custom",
  "lookup",
] as const satisfies ReadonlyArray<MergeFieldMapping["sourceType"]>;

export interface AutoresponderAction {
  enabled: boolean;
  template_id?: string;
  // Optional. When present, the edge function resolves each mapping and
  // folds the result into SendGrid dynamic_template_data alongside the
  // implicit first_name/last_name/email payload. Explicit mappings
  // override the implicit fields.
  variable_mappings?: MergeFieldMapping[];
}

export interface AddToListsAction {
  enabled: boolean;
  list_ids?: string[];
}

export interface AddTagsAction {
  enabled: boolean;
  tags?: string[];
}

export interface NotifyUsersAction {
  enabled: boolean;
  profile_ids?: string[];
}

// Enroll the submitter into one or more ACTIVE message sequences. Explicit
// form→sequence enrollment bypasses the sequence's trigger_conditions (the
// operator's form→sequence choice IS the audience); branch logic and email
// suppression still apply when steps send.
export interface EnrollInSequencesAction {
  enabled: boolean;
  sequence_ids?: string[];
}

export interface CompletionActions {
  autoresponder?: AutoresponderAction;
  add_to_lists?: AddToListsAction;
  add_tags?: AddTagsAction;
  notify_users?: NotifyUsersAction;
  enroll_in_sequences?: EnrollInSequencesAction;
}

export interface FormHandler {
  id: string;
  name: string;
  is_active: boolean;
  success_url: string;
  field_mappings: FieldMapping[];
  completion_actions: CompletionActions;
  submission_count: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface FormSubmission {
  id: string;
  handler_id: string;
  raw_payload?: Record<string, unknown>;
  mapped_fields?: Record<string, unknown>;
  contact_id?: string;
  contact_created: boolean;
  actions_performed?: Record<string, unknown>;
  status: "pending" | "success" | "error";
  error_message?: string;
  ip_address?: string;
  submitted_at: string;
  // Joined via select
  contacts?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface CreateFormHandlerDTO {
  name: string;
  success_url: string;
  field_mappings: FieldMapping[];
  completion_actions: CompletionActions;
}

export interface UpdateFormHandlerDTO {
  name?: string;
  is_active?: boolean;
  success_url?: string;
  field_mappings?: FieldMapping[];
  completion_actions?: CompletionActions;
}

// Allowlisted contact fields for field mappings
export const ALLOWED_CONTACT_FIELDS: readonly string[] = [
  "email",
  "first_name",
  "last_name",
  "phone_number",
  "state",
  "postal_code",
  "city",
  "address_line1",
  "country",
  "title",
  "source",
  "notes",
  "email_opt_in",
  "sms_opt_in",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "state_poster",
  "promo_code",
  "referral_code",
  "tags",
] as const;

export const ALLOWED_CONTACT_FIELD_LABELS: Record<string, string> = {
  email: "Email",
  first_name: "First Name",
  last_name: "Last Name",
  phone_number: "Phone Number",
  state: "State",
  postal_code: "Postal Code",
  city: "City",
  address_line1: "Address Line 1",
  country: "Country",
  title: "Title / Company",
  source: "Source",
  notes: "Notes",
  email_opt_in: "Email Opt-In",
  sms_opt_in: "SMS Opt-In",
  utm_source: "UTM Source",
  utm_medium: "UTM Medium",
  utm_campaign: "UTM Campaign",
  utm_term: "UTM Term",
  utm_content: "UTM Content",
  state_poster: "State Poster",
  promo_code: "Promo Code",
  referral_code: "Referral Code",
  tags: "Tags",
};

export const DEFAULT_FIELD_MAPPINGS: FieldMapping[] = [
  { form_field: "email", contact_field: "email" },
  { form_field: "first_name", contact_field: "first_name" },
  { form_field: "last_name", contact_field: "last_name" },
  { form_field: "phone", contact_field: "phone_number" },
  { form_field: "State", contact_field: "state" },
  { form_field: "zipcode", contact_field: "postal_code" },
  { form_field: "Company", contact_field: "title" },
  { form_field: "consent_to_sms", contact_field: "sms_opt_in" },
  { form_field: "email_optin", contact_field: "email_opt_in" },
  { form_field: "utm_source", contact_field: "utm_source" },
  { form_field: "utm_medium", contact_field: "utm_medium" },
  { form_field: "utm_campaign", contact_field: "utm_campaign" },
  { form_field: "utm_term", contact_field: "utm_term" },
  { form_field: "utm_content", contact_field: "utm_content" },
  { form_field: "StatePoster", contact_field: "state_poster" },
  { form_field: "instructor_promo", contact_field: "promo_code" },
  { form_field: "Ref", contact_field: "referral_code" },
];
